import React, { useEffect, useMemo, useState } from "react";
import { http } from "../../lib/http";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Search, ShieldAlert, Trash2, History, FileText, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";

export default function AdminGDPRPage() {
  return (
    <div data-testid="admin-gdpr-page">
      <PageHeader
        title="GDPR Compliance"
        subtitle="Right-of-deletion / anonymisation requests and history."
      />
      <PageBody>
        <Tabs defaultValue="request" className="w-full">
          <TabsList>
            <TabsTrigger value="request" data-testid="gdpr-tab-request">
              <ShieldAlert className="h-4 w-4 mr-1" />New request
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="gdpr-tab-history">
              <History className="h-4 w-4 mr-1" />History
            </TabsTrigger>
          </TabsList>
          <TabsContent value="request"><RequestTab /></TabsContent>
          <TabsContent value="history"><HistoryTab /></TabsContent>
        </Tabs>
      </PageBody>
    </div>
  );
}

function RequestTab() {
  const [q, setQ] = useState("");
  const [families, setFamilies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [action, setAction] = useState("anonymise");
  const [busy, setBusy] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      http.get(`/families?q=${encodeURIComponent(q)}&limit=15`).then(({ data }) => {
        setFamilies(data.items || []);
      });
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const onPick = async (f) => {
    setSelected(f); setPreview(null); setSuccessInfo(null); setLoading(true);
    try {
      const { data } = await http.post(`/admin/gdpr/preview/${f.id}`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setLoading(false);
  };

  const expectedConfirm = useMemo(() => preview?.family_name || selected?.family_name || "", [preview, selected]);
  const canSubmit = reason.trim().length >= 3 && confirmText.trim() === expectedConfirm.trim() && expectedConfirm.length > 0;

  const runAction = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/gdpr/anonymise/${selected.id}`, {
        reason: reason.trim(),
        anonymise_or_delete: action,
      });
      toast.success(`${action === "delete" ? "Deleted" : "Anonymised"} ${selected.family_name}`);
      setSuccessInfo(data);
      setShowConfirm(false);
      setPreview(null);
      setSelected(null);
      setReason(""); setConfirmText("");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "delete_not_eligible") {
        toast.error(`Cannot delete: ${det.invoices_recent} invoice(s) within 4-year retention. Use anonymise.`);
      } else {
        toast.error(JSON.stringify(det) || "Action failed");
      }
    }
    setBusy(false);
  };

  return (
    <div className="grid md:grid-cols-[300px,1fr] gap-6 mt-4">
      <Card className="bg-white p-4 space-y-3" data-testid="gdpr-search-panel">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search family / NIF / email…"
            data-testid="gdpr-search-input"
          />
        </div>
        <div className="border rounded divide-y max-h-[400px] overflow-y-auto">
          {families.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">No families.</div>}
          {families.map(f => (
            <button key={f.id} type="button"
              onClick={() => onPick(f)}
              data-testid={`gdpr-family-${f.id}`}
              className={`w-full text-left p-2 hover:bg-slate-50 text-sm ${selected?.id === f.id ? "bg-sky-50" : ""}`}>
              <div className="font-medium">{f.family_name}</div>
              <div className="text-xs text-slate-500">{f.primary_parent?.email}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="bg-white p-6 space-y-4" data-testid="gdpr-detail-panel">
        {successInfo ? (
          <SuccessPanel info={successInfo} />
        ) : !selected ? (
          <div className="text-slate-500 text-sm text-center py-12">
            Pick a family on the left to start a GDPR request.
          </div>
        ) : (
          <>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Selected family</div>
              <div className="font-serif text-2xl" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif", color: "#0A1F44" }}>
                {selected.family_name}
              </div>
              <div className="text-sm text-slate-600">{selected.primary_parent?.email}</div>
            </div>

            {loading && <div className="text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1"/> Loading preview…</div>}

            {preview && (
              <>
                <div className="border rounded p-3 bg-amber-50 text-amber-900 text-sm flex gap-2" data-testid="gdpr-preview-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    GDPR actions are <strong>not fully reversible</strong>.
                    {preview.affected.invoices_recent > 0 && (
                      <> Invoices ({preview.affected.invoices_recent} within 4-year retention) will be redacted but kept for legal compliance.</>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="gdpr-affected-counts">
                  {Object.entries(preview.affected).map(([k, v]) => (
                    <div key={k} className="border rounded p-2 text-center">
                      <div className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
                      <div className="text-2xl font-semibold" style={{ color: v > 0 ? "#C8102E" : "#94a3b8" }}>{v}</div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button className="bg-[#0A1F44]" onClick={() => { setAction("anonymise"); setShowConfirm(true); }}
                    data-testid="gdpr-action-anonymise">
                    <ShieldAlert className="h-4 w-4 mr-2"/>Anonymise (recommended)
                  </Button>
                  <Button variant="outline" disabled={!preview.delete_eligible}
                    onClick={() => { setAction("delete"); setShowConfirm(true); }}
                    data-testid="gdpr-action-delete">
                    <Trash2 className="h-4 w-4 mr-2"/>Delete{!preview.delete_eligible && " (blocked)"}
                  </Button>
                </div>
                {!preview.delete_eligible && (
                  <div className="text-xs text-slate-500">
                    Hard delete is blocked because at least one invoice is within the 4-year legal retention window. Use anonymise instead.
                  </div>
                )}
              </>
            )}
          </>
        )}

        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent data-testid="gdpr-confirm-dialog">
            <DialogHeader>
              <DialogTitle>{action === "delete" ? "Confirm hard delete" : "Confirm anonymisation"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p>Type the family name <strong>exactly</strong> to confirm:</p>
              <div className="text-xs text-slate-500">Expected: <code className="bg-slate-100 px-1 rounded">{expectedConfirm}</code></div>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)}
                placeholder="Family name"
                data-testid="gdpr-confirm-name"/>
              <p>Reason (audit-logged):</p>
              <Textarea value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Right-of-erasure request received via email 2026-…"
                data-testid="gdpr-confirm-reason"/>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button onClick={runAction} disabled={!canSubmit || busy}
                className={action === "delete" ? "bg-rose-700" : "bg-[#0A1F44]"}
                data-testid="gdpr-confirm-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : null}
                {action === "delete" ? "Delete — cannot be undone" : "Anonymise — cannot be fully undone"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}

function SuccessPanel({ info }) {
  const downloadReport = async () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.token");
    const r = await fetch(`${base}/api/poc/sharepoint/download/${info.report_file_id}`,
      { headers: { Authorization: `Bearer ${token}` }});
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = info.report_filename || "gdpr_report.pdf"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-3" data-testid="gdpr-success-panel">
      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">{info.action}</Badge>
      <h3 className="font-serif text-xl" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
        Action completed
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(info.affected || {}).map(([k, v]) => (
          <div key={k} className="border rounded p-2 text-center">
            <div className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
            <div className="text-xl font-semibold">{v}</div>
          </div>
        ))}
      </div>
      <Button onClick={downloadReport} variant="outline" data-testid="gdpr-download-report">
        <FileText className="h-4 w-4 mr-2"/>Download report PDF
      </Button>
    </div>
  );
}

function HistoryTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    http.get("/admin/gdpr/history").then(({ data }) => setItems(data.items || []));
  }, []);
  return (
    <div className="mt-4" data-testid="gdpr-history">
      <Card className="bg-white">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Action</th>
              <th className="text-left p-3">Family</th>
              <th className="text-left p-3">Admin</th>
              <th className="text-left p-3">Affected</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="text-center p-6 text-slate-400">No GDPR actions yet.</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-b hover:bg-slate-50" data-testid={`gdpr-history-row-${a.id}`}>
                <td className="p-3 font-mono text-xs">{a.ts?.slice(0, 19).replace("T", " ")}</td>
                <td className="p-3"><Badge>{a.action}</Badge></td>
                <td className="p-3">{a.before?.family_name || a.entity_id}</td>
                <td className="p-3">{a.user_email}</td>
                <td className="p-3 text-xs text-slate-600">
                  {Object.entries(a.after?.affected || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
